import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Line, TransformControls } from '@react-three/drei';
import * as THREE from 'three';

import { hideCaptureHelpers, restoreCaptureHelpers } from './studio-capture';
import type { CamView } from './camera-angles';

/**
 * Shared "camera as a scene object" plumbing for the 3D studios (Phase 3).
 *
 * The SHOT camera (what gets captured) is decoupled from the editor view:
 *   - 'shot' mode → you look through the camera and frame the shot (default).
 *   - 'edit' mode → free view to work on the scene; the shot stays framed and is
 *     shown as a CameraObject + a live PiPView of exactly what it captures.
 *
 * The capture always renders from the shot camera, regardless of the editor view.
 */

// Re-exported so existing importers of StudioCamera keep working; the angle
// table itself lives in camera-angles.ts, which carries no 3D dependencies.
export type { CamView } from './camera-angles';
export {
    NAMED_CAMERA_ANGLES, resolveNamedCameraAngle, resolveRoomInteriorAngle,
    parseMeasuredCamera, resolveMeasuredRoomCamera,
} from './camera-angles';
export type { MeasuredCamera } from './camera-angles';

/** Wide focal length used for the FREE editor view so the whole scene + the shot
 *  camera object stay visible — independent of the shot's lens (which may be a
 *  tight telephoto). The shot lens is re-applied only for the capture + PiP. */
export const EDITOR_FOCAL_LENGTH = 28;

/** Describe the camera's look-down angle so the worker can build the room in the
 *  same perspective. Accepts anything exposing getPolarAngle() (OrbitControls or
 *  a shim). */
export function describeCameraView(controls: any): string | undefined {
    if (!controls || typeof controls.getPolarAngle !== 'function') return undefined;
    const polarDeg = (controls.getPolarAngle() * 180) / Math.PI;
    const lookDown = 90 - polarDeg; // degrees the camera looks down from horizontal
    if (lookDown < 8) return 'eye level — the camera is horizontal, level with the subject';
    if (lookDown < 22) return 'a slightly elevated angle, looking gently down at the subject';
    if (lookDown < 38) return 'a high angle, clearly looking down at the subject and the floor';
    return 'a steep high angle, looking down on the subject from well above';
}

/**
 * Camera-mode state machine + view helpers shared by both studios. Given the
 * live camera + OrbitControls refs, it tracks the shot / edit views and exposes
 * the operations the widgets need (toggle, capture-from-shot, persist).
 */
export function useShotCamera(opts: {
    cameraRef: React.MutableRefObject<THREE.Camera | null>;
    controlsRef: React.MutableRefObject<any>;
    initialShot: CamView | null;
    initialEdit: CamView | null;
}) {
    const { cameraRef, controlsRef } = opts;
    const [camMode, setCamMode] = useState<'shot' | 'edit'>('shot');
    const shotViewRef = useRef<CamView | null>(opts.initialShot);
    const editViewRef = useRef<CamView | null>(opts.initialEdit);

    const readControls = (): CamView | null => {
        const cam = cameraRef.current as any;
        const c = controlsRef.current;
        if (!cam || !c) return null;
        return { pos: [cam.position.x, cam.position.y, cam.position.z], target: [c.target.x, c.target.y, c.target.z] };
    };
    const applyView = (v: CamView) => {
        const cam = cameraRef.current as any;
        const c = controlsRef.current;
        if (!cam || !c) return;
        cam.position.set(v.pos[0], v.pos[1], v.pos[2]);
        c.target.set(v.target[0], v.target[1], v.target[2]);
        c.update();
    };
    // First switch to free view: back off from the shot so the camera object is
    // visible in frame instead of the editor spawning inside it.
    const defaultEditFrom = (shot: CamView): CamView => {
        const dir = new THREE.Vector3(shot.pos[0] - shot.target[0], shot.pos[1] - shot.target[1], shot.pos[2] - shot.target[2]);
        const p = new THREE.Vector3(shot.target[0], shot.target[1], shot.target[2]).add(dir.multiplyScalar(1.7)).add(new THREE.Vector3(0.8, 1.1, 0.8));
        return { pos: [p.x, p.y, p.z], target: shot.target };
    };
    const toggleCamMode = () => {
        const cur = readControls();
        if (camMode === 'shot') {
            if (cur) shotViewRef.current = cur;
            const next = editViewRef.current || (cur ? defaultEditFrom(cur) : null);
            if (next) applyView(next);
            setCamMode('edit');
        } else {
            if (cur) editViewRef.current = cur;
            if (shotViewRef.current) applyView(shotViewRef.current);
            setCamMode('shot');
        }
    };
    /** The view the CAPTURE renders from — live controls in shot mode, the saved
     *  shot otherwise (free view never changes the shot). */
    const currentShotView = (): CamView | null => (camMode === 'shot' ? (readControls() || shotViewRef.current) : shotViewRef.current);
    const shotCameraViewText = (): string | undefined => {
        if (camMode === 'shot') return describeCameraView(controlsRef.current);
        const shot = shotViewRef.current;
        if (!shot) return describeCameraView(controlsRef.current);
        const horiz = Math.hypot(shot.pos[0] - shot.target[0], shot.pos[2] - shot.target[2]);
        const polar = Math.atan2(horiz, shot.pos[1] - shot.target[1]); // angle from +Y
        return describeCameraView({ getPolarAngle: () => polar });
    };
    /** Move the live camera to the shot view + shot lens for a capture (edit mode
     *  only — the editor view uses a wide lens) and return a restore fn. In shot
     *  mode the live camera IS the shot, so it just syncs the shot ref. */
    const captureFromShot = (camera: THREE.Camera | null, focalLength?: number): (() => void) => {
        if (camMode === 'shot') { const cur = readControls(); if (cur) shotViewRef.current = cur; return () => { }; }
        const shot = shotViewRef.current;
        if (!camera || !shot) return () => { };
        const cam = camera as any;
        const savedPos = cam.position.clone();
        const savedQuat = cam.quaternion.clone();
        const applyFL = cam.isPerspectiveCamera && typeof focalLength === 'number';
        const savedFL = applyFL ? cam.getFocalLength() : null;
        cam.position.set(shot.pos[0], shot.pos[1], shot.pos[2]);
        cam.lookAt(new THREE.Vector3(shot.target[0], shot.target[1], shot.target[2]));
        if (applyFL) { cam.setFocalLength(focalLength); cam.updateProjectionMatrix(); }
        cam.updateMatrixWorld(true);
        return () => {
            cam.position.copy(savedPos); cam.quaternion.copy(savedQuat);
            if (savedFL != null) { cam.setFocalLength(savedFL); cam.updateProjectionMatrix(); }
            cam.updateMatrixWorld(true);
        };
    };
    /** Views to persist into the session (shot = what reopens; edit kept aside). */
    const saveViews = (): { shot: CamView | null; edit: CamView | null } => {
        const cur = readControls();
        return {
            shot: camMode === 'shot' ? (cur || shotViewRef.current) : shotViewRef.current,
            edit: camMode === 'edit' ? (cur || editViewRef.current) : editViewRef.current,
        };
    };

    return { camMode, setCamMode, shotViewRef, editViewRef, toggleCamMode, currentShotView, shotCameraViewText, captureFromShot, saveViews };
}

/** The SHOT camera visualised in the scene (free view only): a small graphite
 *  camera body + lens and the view frustum out to a framed rectangle. Click it
 *  to look through the camera. Tagged studioHelper so it never appears in the
 *  capture or the PiP preview. */
export const CameraObject: React.FC<{
    view: CamView;
    focalLength: number;
    aspect: number;
    onEnter: () => void;
    /** When true, a move/rotate gizmo is attached so the camera can be posed. */
    editable?: boolean;
    gizmoMode?: 'translate' | 'rotate';
    /** Fired as the gizmo moves/rotates the camera — reports the new shot view. */
    onViewChange?: (v: CamView) => void;
}> = ({ view, focalLength, aspect, onEnter, editable, gizmoMode = 'translate', onViewChange }) => {
    const ref = useRef<THREE.Group>(null);
    const [obj, setObj] = useState<THREE.Group | null>(null);
    const [hover, setHover] = useState(false);
    // Keep the camera→target distance so a rotate re-aims (pans/tilts) and a
    // translate dollies the whole view, both preserving how far the target sits.
    const distRef = useRef(1);
    useEffect(() => {
        const g = ref.current;
        if (!g) return;
        g.position.set(view.pos[0], view.pos[1], view.pos[2]);
        // Object3D.lookAt points the group's +Z at the target — the lens + frustum
        // below are built along +Z.
        g.lookAt(new THREE.Vector3(view.target[0], view.target[1], view.target[2]));
        distRef.current = Math.max(0.3, Math.hypot(view.target[0] - view.pos[0], view.target[1] - view.pos[1], view.target[2] - view.pos[2]));
    }, [view]);
    // Read the gizmo-driven transform back out as a shot view (target = position +
    // forward × distance). The lookAt on capture/PiP re-levels any roll.
    const emitChange = () => {
        const g = ref.current;
        if (!g || !onViewChange) return;
        const fwd = new THREE.Vector3(0, 0, 1).applyQuaternion(g.quaternion).normalize();
        const p = g.position;
        const tgt = p.clone().add(fwd.multiplyScalar(distRef.current));
        onViewChange({ pos: [p.x, p.y, p.z], target: [tgt.x, tgt.y, tgt.z] });
    };
    // Frustum out to a frame rectangle: vertical half-angle from the focal length
    // (35mm film height 24mm → 12mm half), width from the shot aspect.
    const d = 1.15;
    const hh = d * Math.tan(Math.atan(12 / Math.max(1, focalLength)));
    const hw = hh * Math.max(0.2, aspect);
    const corners: Array<[number, number, number]> = [
        [-hw, -hh, d], [hw, -hh, d], [hw, hh, d], [-hw, hh, d],
    ];
    const lineProps = { color: '#111827', lineWidth: 1, transparent: true, opacity: hover ? 0.75 : 0.45, depthTest: false } as const;
    const bodyColor = hover ? '#111827' : '#374151';
    return (
        <>
            <group ref={(g) => { ref.current = g; if (g !== obj) setObj(g); }} userData={{ studioHelper: true }}>
                <mesh
                    position={[0, 0, -0.17]}
                    onClick={(e: any) => { e.stopPropagation(); onEnter(); }}
                    onPointerOver={(e: any) => { e.stopPropagation(); setHover(true); document.body.style.cursor = 'pointer'; }}
                    onPointerOut={() => { setHover(false); document.body.style.cursor = ''; }}
                >
                    <boxGeometry args={[0.34, 0.24, 0.3]} />
                    <meshStandardMaterial color={bodyColor} roughness={0.55} metalness={0.1} />
                </mesh>
                <mesh position={[0, 0, 0.04]} rotation={[Math.PI / 2, 0, 0]}>
                    <cylinderGeometry args={[0.09, 0.09, 0.14, 20]} />
                    <meshStandardMaterial color={hover ? '#111827' : '#1F2937'} roughness={0.4} metalness={0.2} />
                </mesh>
                {/* Frustum edges + the frame rectangle. */}
                {corners.map((c, i) => (
                    <Line key={i} points={[[0, 0, 0.1], c]} {...lineProps} />
                ))}
                <Line points={[...corners, corners[0]]} {...lineProps} />
            </group>
            {editable && obj && (
                <TransformControls object={obj} mode={gizmoMode} size={0.62} onObjectChange={emitChange} />
            )}
        </>
    );
};

/** Force-release the WebGL context when the Canvas unmounts. Browsers cap live
 *  contexts (~8-16); without an explicit release, opening/closing the studio many
 *  times leaks contexts until "Error creating WebGL context". R3F disposes the
 *  renderer but doesn't always free the context slot — forceContextLoss does.
 *
 *  DEFERRED: the release must run AFTER React/three finish disposing this
 *  teardown's scene resources (the <Environment> cube render target, textures…).
 *  Releasing synchronously frees the renderer first, so those disposals read a
 *  freed WebGL state and throw in deallocateRenderTarget. A macrotask runs after
 *  the synchronous unmount phase completes. We do NOT call gl.dispose() — R3F
 *  already does, and double-disposing is what caused that crash. */
export const ReleaseContextOnUnmount: React.FC = () => {
    const gl = useThree((s) => s.gl);
    useEffect(() => () => {
        const renderer = gl;
        setTimeout(() => { try { renderer.forceContextLoss(); } catch { /* already gone */ } }, 0);
    }, [gl]);
    return null;
};

// ─── Camera path animation (viewport video capture) ─────────────────────

/** One keyframe of a camera fly-through: a shot view + its lens. */
export interface ShotKeyframe { view: CamView; lensMm: number }

/** A pending fly-through job — set the ref to start; cleared when done. */
export interface CameraPathJob {
    kfs: ShotKeyframe[];
    /** Total clip duration in seconds (split evenly across segments). */
    duration: number;
    /** Straight-line timing — for dense keyframes sampled from a live take,
     *  where easing every segment would make the camera stutter. */
    linear?: boolean;
    onProgress?: (t: number) => void;
    onDone: () => void;
}

/** Drives the main camera smoothly through a list of keyframes (eased segment
 *  interpolation of position, target and lens). Used while MediaRecorder records
 *  the canvas → the "3D viewport video" a render-to-real model consumes. */
export const CameraPathAnimator: React.FC<{ jobRef: React.MutableRefObject<CameraPathJob | null> }> = ({ jobRef }) => {
    const camera = useThree((s) => s.camera) as THREE.PerspectiveCamera;
    const startRef = useRef<number | null>(null);
    useFrame(() => {
        const job = jobRef.current;
        if (!job) { startRef.current = null; return; }
        if (startRef.current == null) startRef.current = performance.now();
        const t = Math.min(1, (performance.now() - startRef.current) / (job.duration * 1000));
        const segs = Math.max(1, job.kfs.length - 1);
        const sPos = Math.min(segs - 1e-6, t * segs);
        const i = Math.floor(sPos);
        const x = sPos - i;
        const f = job.linear ? x : x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2; // easeInOutCubic per segment
        const a = job.kfs[i];
        const b = job.kfs[Math.min(i + 1, job.kfs.length - 1)];
        const l = (u: number, v: number) => u + (v - u) * f;
        camera.position.set(l(a.view.pos[0], b.view.pos[0]), l(a.view.pos[1], b.view.pos[1]), l(a.view.pos[2], b.view.pos[2]));
        camera.lookAt(l(a.view.target[0], b.view.target[0]), l(a.view.target[1], b.view.target[1]), l(a.view.target[2], b.view.target[2]));
        if ((camera as any).isPerspectiveCamera) {
            camera.setFocalLength(l(a.lensMm, b.lensMm));
            camera.updateProjectionMatrix();
        }
        job.onProgress?.(t);
        if (t >= 1) {
            const done = job.onDone;
            startRef.current = null;
            jobRef.current = null;
            done();
        }
    });
    return null;
};

/** Live picture-in-picture of the SHOT camera's view — a scissored second render
 *  pass into the bottom-left corner of the canvas, after the main pass. Studio
 *  helpers (grid, gizmos, the camera object itself) are hidden for the pass so
 *  the preview shows the shot as it will be captured. */
export const PiPView: React.FC<{
    viewRef: React.MutableRefObject<CamView | null>;
    focalLength: number;
    widthPx?: number;
}> = ({ viewRef, focalLength, widthPx = 190 }) => {
    const pipCam = useMemo(() => new THREE.PerspectiveCamera(35, 1, 0.05, 200), []);
    useFrame(({ gl, scene, size }) => {
        const v = viewRef.current;
        if (!v) return;
        const w = widthPx;
        const h = Math.max(40, Math.round(widthPx * size.height / size.width));
        pipCam.aspect = w / h;
        pipCam.setFocalLength(focalLength);
        pipCam.position.set(v.pos[0], v.pos[1], v.pos[2]);
        pipCam.lookAt(new THREE.Vector3(v.target[0], v.target[1], v.target[2]));
        pipCam.updateProjectionMatrix();
        const hidden: THREE.Object3D[] = [];
        hidden.push(...hideCaptureHelpers(scene));
        const prevAutoClear = gl.autoClear;
        gl.autoClear = false;
        gl.clearDepth();
        gl.setScissorTest(true);
        gl.setScissor(12, 12, w, h);
        gl.setViewport(12, 12, w, h);
        gl.render(scene, pipCam);
        gl.setScissorTest(false);
        gl.setViewport(0, 0, size.width, size.height);
        gl.autoClear = prevAutoClear;
        restoreCaptureHelpers(hidden);
    }, 2); // after the main (composer) pass at priority 1
    return null;
};
